// src/pages/GuestListPage.jsx
import React from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  StarIcon,  
  TagIcon,
  TicketIcon,
  MusicalNoteIcon, // Добавлено для ARTIST
  UserGroupIcon     // Добавлено для STAFF
} from "@heroicons/react/24/solid";
import EventLayout from "../components/EventLayout";
import AddGuestModal from "../components/AddGuestModal";
import EditGuestModal from "../components/EditGuestModal";
import ConfirmModal from "../components/ConfirmModal";
import AddFromPermanentListModal from "../components/AddFromPermanentListModal";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config";
import notoSansRegularUrl from "../assets/fonts/NotoSans-Regular.ttf";

let pdfFontBase64Promise = null;

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return window.btoa(binary);
};

const loadPdfUnicodeFont = async (doc) => {
  if (!pdfFontBase64Promise) {
    pdfFontBase64Promise = fetch(notoSansRegularUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load PDF font");
        }
        return response.arrayBuffer();
      })
      .then(arrayBufferToBase64);
  }

  const fontBase64 = await pdfFontBase64Promise;
  doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
  doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
  doc.setFont("NotoSans", "normal");
};

function GuestListPage() {
  const [showModal, setShowModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [guests, setGuests] = React.useState([]);
  const [guestToEdit, setGuestToEdit] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploadingPdf, setUploadingPdf] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [guestToDelete, setGuestToDelete] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  // For the "Add from List" modal
  const [showAddFromListModal, setShowAddFromListModal] = React.useState(false);
  const [permanentGuestListsOptions, setPermanentGuestListsOptions] = React.useState([]);

  // Sorting states
  const [sortBy, setSortBy] = React.useState("name");
  const [sortOrder, setSortOrder] = React.useState("asc");

  // State for selected guests (checkboxes)
  const [selectedGuestIds, setSelectedGuestIds] = React.useState([]);
  // State for confirming bulk deletion
  const [showConfirmModalMultiple, setShowConfirmModalMultiple] = React.useState(false);
  
  // State for Via filtering
  const [viaFilter, setViaFilter] = React.useState(null);
  const [showViaDropdown, setShowViaDropdown] = React.useState(false);
  
  // State for Category filtering
  const [categoryFilter, setCategoryFilter] = React.useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = React.useState(false);
  
  // Refs for clicking outside dropdowns
  const viaDropdownRef = React.useRef(null);
  const categoryDropdownRef = React.useRef(null);
  const pdfInputRef = React.useRef(null);

  const { id: eventId } = useParams();
  const { user } = useAuth();

  // Функция для отображения иконки категории гостя
  const renderCategoryIcon = (category) => {
    switch (category) {
          case "VIP":
            return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
          case "BACKSTAGE":
            return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
          case "ARTIST":
            return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
          case "STAFF":
            return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
          case "REGULAR":
          default:
            return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
        }
  };

  // Функция для определения, мобильное ли устройство
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Hook to detect clicks outside of an element
  const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    React.useEffect(() => {
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("mousedown", handleClick);
      };
    });
  };

  // Close dropdowns when clicking outside
  useOutsideClick(viaDropdownRef, () => setShowViaDropdown(false));
  useOutsideClick(categoryDropdownRef, () => setShowCategoryDropdown(false));

  // Function to get unique Via values
  const getUniqueViaValues = () => {
    const viaValues = guests
      .map(guest => guest.type || "N/A")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return viaValues;
  };

  // Function to get unique Category values
  const getUniqueCategoryValues = () => {
    const categoryValues = guests
      .map(guest => guest.category || "REGULAR")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return categoryValues;
  };

  // Function to clear Via filter
  const clearViaFilter = () => {
    setViaFilter(null);
    setShowViaDropdown(false);
  };

  // Function to clear Category filter
  const clearCategoryFilter = () => {
    setCategoryFilter(null);
    setShowCategoryDropdown(false);
  };

  const isPercentageDiscountAdmission = (admission) => {
    if (typeof admission !== "string") return false;
    const normalizedAdmission = admission.trim().toUpperCase();
    return normalizedAdmission !== "FREE" && normalizedAdmission.includes("%");
  };

  // Fetch guests from the API
  const fetchGuests = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/?event_id=${eventId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load guest list");
      }

      const data = await response.json();
      setGuests(data);
    } catch (error) {
      console.error("Error loading guests:", error);
      toast.error("Failed to load guest list.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchPermanentGuestListsForModal = React.useCallback(async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-guest-lists/`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load permanent guest lists");
      }

      const data = await response.json();
      const options = data.map((list) => ({
        value: list.id,
        label: list.name,
      }));
      setPermanentGuestListsOptions(options);
    } catch (error) {
      console.error("Error loading permanent guest lists:", error);
      toast.error("Failed to load permanent guest lists.");
    }
  }, []);

  React.useEffect(() => {
    fetchGuests();
    fetchPermanentGuestListsForModal();
  }, [fetchGuests, fetchPermanentGuestListsForModal]);

  const handleSaveGuestList = async (guestData) => {
    try {
      const payload = {
        event_id: eventId,
        names: guestData.names,
        via: guestData.via,
        admission: guestData.admissionType,
        plus: guestData.plus || 0,
        category: guestData.category || "REGULAR", // Добавление категории
      };

      const response = await fetch(`${SERVER_BASE_URL}/api/guests/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create guest");
      }

      setShowModal(false);
      fetchGuests();
      toast.success("Guest added successfully.");
    } catch (error) {
      console.error("Failed to save guest:", error);
      toast.error("Failed to save guest.");
    }
  };

  const handleAddGuestList = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  const handleEditGuest = (guest) => {
    setGuestToEdit(guest);
    setShowEditModal(true);
  };

  const handleUpdateGuest = async (updatedGuestData) => {
    const requestBody = JSON.stringify({
      names: updatedGuestData.names,
      type: updatedGuestData.type,
      admission: updatedGuestData.admission,
      plus: updatedGuestData.plus,
      category: updatedGuestData.category, // Добавление категории
    });

    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/${updatedGuestData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: requestBody,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update guest");
      }

      const updatedGuest = await response.json();

      setGuests((prevGuests) =>
        prevGuests.map((guest) =>
          guest.id === updatedGuest.id ? updatedGuest : guest
        )
      );

      setShowEditModal(false);
      setGuestToEdit(null);
      toast.success("Guest updated successfully.");
    } catch (error) {
      console.error("Failed to update guest:", error);
      toast.error("Failed to update guest.");
    }
  };

  const handleDeleteGuest = async () => {
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/${guestToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete guest");
      }

      fetchGuests();
      setShowConfirmModal(false);
      setGuestToDelete(null);
      toast.success("Guest deleted successfully.");
    } catch (error) {
      console.error("Error deleting guest:", error);
      toast.error("Failed to delete guest.");
    }
  };

  const handleDeleteClick = (guestId) => {
    setGuestToDelete(guestId);
    setShowConfirmModal(true);
  };

  const calculateSummary = () => {
    const totalGuests = guests.length;
    const freeGuests = guests.filter(
      (guest) => (guest.admission || "").trim().toUpperCase() === "FREE"
    ).length;
    const discountGuests = guests.filter((guest) =>
      isPercentageDiscountAdmission(guest.admission)
    ).length;
    const totalPluses = guests.reduce((sum, guest) => sum + guest.plus, 0);
    return { totalGuests, freeGuests, discountGuests, totalPluses };
  };

  const summary = calculateSummary();

  // === Sorting functions ===
  const handleSort = (criteria) => {
    if (criteria === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortBy) {
      return <span className="ml-1 text-gray-400">–</span>;
    }
    return sortOrder === "asc" ? (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▲
      </span>
    ) : (
      <span className="ml-1 text-gray-600" style={{ fontSize: "11px" }}>
        ▼
      </span>
    );
  };

  const sortedGuests = [...guests].sort((a, b) => {
    if (sortBy === "name") {
      return sortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (sortBy === "plus") {
      return sortOrder === "asc" ? a.plus - b.plus : b.plus - a.plus;
    }
    if (sortBy === "via") {
      return sortOrder === "asc"
        ? (a.type || "").localeCompare(b.type || "")
        : (b.type || "").localeCompare(a.type || "");
    }
    if (sortBy === "admission") {
      return sortOrder === "asc"
        ? a.admission.localeCompare(b.admission)
        : b.admission.localeCompare(a.admission);
    }
    if (sortBy === "category") {
      return sortOrder === "asc"
        ? (a.category || "REGULAR").localeCompare(b.category || "REGULAR")
        : (b.category || "REGULAR").localeCompare(a.category || "REGULAR");
    }
    return 0;
  });

  // Enhanced filtering with Via and Category filters
  const filteredGuests = sortedGuests.filter((guest) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      guest.name.toLowerCase().includes(query) ||
      (guest.type && guest.type.toLowerCase().includes(query)) ||
      (guest.admission && guest.admission.toLowerCase().includes(query)) ||
      guest.plus.toString().includes(query) ||
      (guest.category && guest.category.toLowerCase().includes(query));
    
    // Apply Via filter if active
    const matchesViaFilter = !viaFilter || guest.type === viaFilter;
    
    // Apply Category filter if active
    const matchesCategoryFilter = !categoryFilter || guest.category === categoryFilter;
    
    return matchesSearch && matchesViaFilter && matchesCategoryFilter;
  });

  // === Functions for "Add from List" ===
  const handleShowAddFromListModal = () => {
    setShowAddFromListModal(true);
  };

  const handleCloseAddFromListModal = () => {
    setShowAddFromListModal(false);
  };

  const handleAddGuestsFromList = async (selectedListIds) => {
    if (selectedListIds.length === 0) return;
  
    try {
      setLoading(true);
  
      for (const listId of selectedListIds) {
        const response = await fetch(
          `${SERVER_BASE_URL}/api/permanent-guest-lists/${listId}/guests/`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        );
  
        if (!response.ok) {
          throw new Error(`Failed to load guests from list ${listId}`);
        }
  
        const data = await response.json();
        // Добавляем логирование чтобы увидеть, какие данные приходят от API
        console.log("Permanent guests data:", data);
        
        for (const guestFromList of data) {
          // Убедимся, что категория берется из правильного поля
          const category = guestFromList.category || "REGULAR";
          console.log(`Guest ${guestFromList.name} has category: ${category}`);
          
          const guestDataForEvent = {
            event_id: eventId,
            names: guestFromList.name,
            via: guestFromList.via || "N/A",
            admission: guestFromList.admission_type || "FREE",
            plus: guestFromList.plus || 0,
            category: category, // Явно указываем категорию
          };
          
          console.log("Importing guest with data:", guestDataForEvent);
          await handleSaveGuestListForImport(guestDataForEvent);
        }
      }
  
      fetchGuests();
      setShowAddFromListModal(false);
      toast.success("Guests from selected lists added successfully.");
    } catch (error) {
      console.error("Error adding guests from lists:", error);
      toast.error("Failed to add guests from selected lists.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGuestListForImport = async (guestData) => {
    try {
      // Приводим категорию к верхнему регистру
      const category = guestData.category ? guestData.category.toUpperCase() : "REGULAR";
      
      const payload = {
        event_id: eventId,
        names: guestData.names,
        via: guestData.via,
        admission: guestData.admission,
        plus: guestData.plus || 0,
        category: category, // Используем преобразованную категорию
      };
  
      console.log("Sending payload to create guest:", payload);
  
      const response = await fetch(`${SERVER_BASE_URL}/api/guests/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error:", errorText);
        throw new Error("Failed to create guest during import");
      }
      
      // Получаем и логируем ответ сервера для отладки
      const responseData = await response.json();
      console.log("Server response:", responseData);
    } catch (error) {
      console.error("Failed to save guest during import:", error);
      toast.error("Failed to save guest during import.");
    }
  };

  const handleUploadPdfClick = () => {
    pdfInputRef.current?.click();
  };

  const handlePdfUpload = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";

    if (!file) return;

    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please upload a PDF file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploadingPdf(true);

    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/api/guests/import-pdf?event_id=${eventId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.detail || "Failed to import PDF.");
      }

      if (result.imported > 0) {
        await fetchGuests();
        toast.success(
          `Imported ${result.imported} guests. Skipped ${result.skipped_duplicates || 0} duplicates.`
        );
      } else {
        toast.info(`No new guests found. Skipped ${result.skipped_duplicates || 0} duplicates.`);
      }
    } catch (error) {
      console.error("Failed to import PDF:", error);
      toast.error(error.message || "Failed to import PDF.");
    } finally {
      setUploadingPdf(false);
    }
  };

  const downloadPDF = async () => {
    const doc = new jsPDF({ putOnlyUsedFonts: true });

    try {
      await loadPdfUnicodeFont(doc);
    } catch (error) {
      console.error("Failed to load PDF font:", error);
      toast.error("Failed to prepare PDF export.");
      return;
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10; // top and bottom margins
    const headerHeight = 10; // approximate header height for the table
    const desiredRows = 50; // desired number of rows per page

    // Calculate a fixed row height so that exactly 50 rows fit on the page
    const availableHeight = pageHeight - margin * 2 - headerHeight;
    const fixedRowHeight = availableHeight / desiredRows;

    // Sorting: first by "via" (using guest.type), then by name (always ascending)
    const sortedForPDF = [...filteredGuests].sort((a, b) => {
      const viaA = a.type || "";
      const viaB = b.type || "";
      const viaComparison = viaA.localeCompare(viaB);
      if (viaComparison !== 0) {
        return viaComparison;
      }
      return a.name.localeCompare(b.name);
    });

    // Build table rows; add empty rows if there are fewer than 50 entries
    const bodyRows = sortedForPDF.map((guest) => [
      guest.name,
      guest.plus,
      guest.type || "N/A",
      guest.admission,
      guest.category || "REGULAR", // Добавление категории
    ]);
    while (bodyRows.length < desiredRows) {
      bodyRows.push(["", "", "", "", ""]);
    }

    // Add title with filter information if Via or Category filter is active
    let title = `Guest List - ${eventId}`;
    if (viaFilter) {
      title += ` - Via: ${viaFilter}`;
    }
    if (categoryFilter) {
      title += ` - Category: ${categoryFilter}`;
    }

    doc.autoTable({
      startY: margin,
      head: [["Name", "Plus", "Via", "Admission", "Category"]],
      body: bodyRows,
      styles: {
        font: "NotoSans",
        fontStyle: "normal",
        cellPadding: 1,
        fontSize: 8,
        minCellHeight: fixedRowHeight,
        overflow: "linebreak",
      },
      headStyles: {
        font: "NotoSans",
        fontStyle: "normal",
      },
      didDrawPage: function() {
        // Add title at the top of the page
        doc.setFont("NotoSans", "normal");
        doc.setFontSize(10);
        doc.text(title, margin, margin - 2);
      },
      didParseCell: function (data) {
        if (data.row.section === "body") {
          data.cell.height = fixedRowHeight;
          data.row.height = fixedRowHeight;
          data.cell.styles.minCellHeight = fixedRowHeight;
        }
      },
      pageBreak: "never",
    });

    // Генерация имени файла с учетом фильтров
    let filename = `guest_list_${eventId}`;
    if (viaFilter) {
      filename += `_via_${viaFilter.replace(/[^a-z0-9]/gi, '_')}`;
    }
    if (categoryFilter) {
      filename += `_cat_${categoryFilter.replace(/[^a-z0-9]/gi, '_')}`;
    }
    filename += '.pdf';
    
    doc.save(filename);
  };

  // === Functions for handling checkboxes ===
  const handleCheckboxChange = (guestId) => {
    setSelectedGuestIds((prev) =>
      prev.includes(guestId)
        ? prev.filter((id) => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleSelectAllChange = () => {
    if (selectedGuestIds.length === filteredGuests.length) {
      setSelectedGuestIds([]);
    } else {
      setSelectedGuestIds(filteredGuests.map((guest) => guest.id));
    }
  };

  // === Function for deleting selected guests ===
  const handleDeleteSelected = async () => {
    try {
      const deletePromises = selectedGuestIds.map((id) =>
        fetch(`${SERVER_BASE_URL}/api/guests/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
      );
      await Promise.all(deletePromises);
      fetchGuests();
      setSelectedGuestIds([]);
      setShowConfirmModalMultiple(false);
      toast.success("Selected guests deleted successfully.");
    } catch (error) {
      console.error("Error deleting selected guests:", error);
      toast.error("Failed to delete selected guests.");
    }
  };

  return (
    <>
      <EventLayout hideDescription>
        <div className="container mx-auto max-w-5xl px-0 sm:px-4 py-4" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>  
          {/* Карточка верхнего блока в стиле DashboardPage */}
          <div className="bg-white rounded-lg shadow-md p-2 px-2 sm:p-4 mb-6 mx-0">
            <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center">
              <div className="flex flex-row gap-2 mb-2 sm:mb-0 w-full sm:w-auto">
                <button
                  onClick={handleAddGuestList}
                  className="w-full sm:w-auto flex items-center justify-center bg-indigo-600 text-white font-bold px-3 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
                >
                  <PlusIcon className="h-4 w-4 sm:mr-2" />
                  <span className="sm:inline hidden">Add</span> Guest
                </button>

                <button
                  onClick={handleShowAddFromListModal}
                  className="w-full sm:w-auto flex items-center justify-center bg-indigo-600 text-white font-bold px-3 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
                >
                  <PlusIcon className="h-4 w-4 sm:mr-2" />
                  <span className="sm:inline hidden">Add from</span> List
                </button>
              </div>

              <div className="w-full sm:flex-1 flex sm:ml-2 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Search guests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Статистика - адаптивная для мобильных и десктопа */}
            
            {/* Десктопная версия */}
            <div className="hidden sm:grid sm:grid-cols-4 sm:gap-2 mb-4">
              <div className="py-2 px-3 rounded-lg  bg-indigo-50">
                <p className="text-sm text-gray-600 mb-1">Total Guests</p>
                <p className="text-xl font-bold text-gray-800 flex items-center">
                  <span>{summary.totalGuests}</span>
                </p>
              </div>
              
              <div className="py-2 px-3 rounded-lg bg-indigo-50">
                <p className="text-sm text-gray-600 mb-1">Free</p>
                <p className="text-xl font-bold text-gray-800 flex items-center">
                  <span>{summary.freeGuests}</span>
                </p>
              </div>
              
              <div className="py-2 px-3 rounded-lg  bg-indigo-50">
                <p className="text-sm text-gray-600 mb-1">Discount</p>
                <p className="text-xl font-bold text-gray-800 flex items-center">
                  <span>{summary.discountGuests}</span>
                </p>
              </div>
              
              <div className="py-2 px-3 rounded-lg  bg-indigo-50">
                <p className="text-sm text-gray-600 mb-1">Pluses</p>
                <p className="text-xl font-bold text-gray-800 flex items-center">
                  <span>{summary.totalPluses}</span>
                </p>
              </div>
            </div>

            {/* Мобильная версия */}
            <div className="flex justify-evenly mb-4 gap-0 sm:hidden">
              <div className="py-2 rounded-lg bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
                <p className="text-[10px] text-gray-600 m-0 text-center">Total</p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {summary.totalGuests}
                </p>
              </div>
              
              <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
                <p className="text-[10px] text-gray-600 m-0 text-center">Free</p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {summary.freeGuests}
                </p>
              </div>
              
              <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
                <p className="text-[10px] text-gray-600 m-0 text-center">Discount</p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {summary.discountGuests}
                </p>
              </div>
              
              <div className="py-2 rounded-lg  bg-indigo-50 w-[23%] flex flex-col justify-center px-1">
                <p className="text-[10px] text-gray-600 m-0 text-center">Pluses</p>
                <p className="text-lg font-bold text-gray-800 text-center m-0">
                  {summary.totalPluses}
                </p>
              </div>
            </div>
            
            {/* Кнопки действий: сгруппированы вместе */}
            <div className="flex flex-wrap gap-2 justify-between items-center">
              {/* Действия с выбранными гостями */}
              <div className="flex items-center gap-2">
                {selectedGuestIds.length > 0 ? (
                  <button
                    onClick={() => setShowConfirmModalMultiple(true)}
                    className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    ({selectedGuestIds.length})
                  </button>
                ) : viaFilter ? (
                  <div className="flex items-center bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md border border-indigo-200 text-sm">
                    <span>Via: <strong>{viaFilter}</strong></span>
                    <button 
                      onClick={clearViaFilter}
                      className="ml-2 text-indigo-500 hover:text-indigo-700"
                    >
                      <span>×</span>
                    </button>
                  </div>
                ) : categoryFilter ? (
                  <div className="flex items-center bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md border border-indigo-200 text-sm">
                    <span>Category: <strong>{categoryFilter}</strong></span>
                    <button 
                      onClick={clearCategoryFilter}
                      className="ml-2 text-indigo-500 hover:text-indigo-700"
                    >
                      <span>×</span>
                    </button>
                  </div>
                ) : null}
              </div>

              {/* PDF экспорт и Via фильтр */}
              <div className="flex items-center gap-2 relative">
                {/* Category Filter */}
                <div ref={categoryDropdownRef}>
                  <button
                    onClick={() => setShowCategoryDropdown(prev => !prev)}
                    className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                  >
                    <span>Category Filter</span>
                    <span className="ml-1">{showCategoryDropdown ? '▲' : '▼'}</span>
                  </button>
                  
                  {showCategoryDropdown && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                      <ul className="py-1">
                        <li 
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100"
                          onClick={clearCategoryFilter}
                        >
                          Show All
                        </li>
                        {getUniqueCategoryValues().map((category) => (
                          <li 
                            key={category} 
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${categoryFilter === category ? 'bg-indigo-50 text-indigo-700' : ''}`}
                            onClick={() => {
                              setCategoryFilter(category);
                              setShowCategoryDropdown(false);
                            }}
                          >
                            {category}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Via Filter */}
                <div ref={viaDropdownRef}>
                  <button
                    onClick={() => setShowViaDropdown(prev => !prev)}
                    className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                  >
                    <span>Via Filter</span>
                    <span className="ml-1">{showViaDropdown ? '▲' : '▼'}</span>
                  </button>
                  
                  {showViaDropdown && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 max-h-60 overflow-y-auto">
                      <ul className="py-1">
                        <li 
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100"
                          onClick={clearViaFilter}
                        >
                          Show All
                        </li>
                        {getUniqueViaValues().map((via) => (
                          <li 
                            key={via} 
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${viaFilter === via ? 'bg-indigo-50 text-indigo-700' : ''}`}
                            onClick={() => {
                              setViaFilter(via);
                              setShowViaDropdown(false);
                            }}
                          >
                            {via}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handlePdfUpload}
                />
                <button
                  onClick={handleUploadPdfClick}
                  disabled={uploadingPdf}
                  className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                  {uploadingPdf ? "Uploading..." : "Upload PDF"}
                </button>

                <button
                  onClick={downloadPDF}
                  className="flex items-center bg-white text-gray-700 px-3 py-2 rounded-md hover:bg-gray-50 border border-gray-300 transition duration-200 text-sm"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                  PDF
                </button>
              </div>
            </div>
          </div>

          {/* Таблица гостей */}
          <div className="relative bg-white rounded-none sm:rounded-lg shadow-md overflow-hidden mx-0">
            {loading ? (
              <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-10">
                <Spinner />
              </div>
            ) : filteredGuests.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No guests found for this event.</p>
              </div>
            ) : (
              <>
                {/* Mobile header with sorting and select all */}
                <div className="sm:hidden flex justify-between items-center py-1.5 px-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex space-x-4 items-center">
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("name")}
                    >
                      Name {renderSortIcon("name")}
                    </div>
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("category")}
                    >
                      Cat {renderSortIcon("category")}
                    </div>
                    <div
                      className="text-sm font-medium cursor-pointer"
                      onClick={() => handleSort("via")}
                    >
                      Via {renderSortIcon("via")}
                    </div>
                  </div>
                  <div className="flex items-center">
                   <input
                     type="checkbox"
                     id="select-all-mobile"
                     className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                     checked={
                       selectedGuestIds.length === filteredGuests.length &&
                       filteredGuests.length > 0
                     }
                     onChange={handleSelectAllChange}
                   />
                 </div>
               </div>

               {/* Desktop table header */}
               <div className="hidden sm:grid grid-cols-[40px_2fr_1fr_1.5fr_1fr_1fr_80px] gap-1 bg-gray-50 py-1.5 px-2 border-b border-gray-200">
                 <div className="flex justify-center items-center">
                   <input
                     type="checkbox"
                     checked={
                       selectedGuestIds.length === filteredGuests.length &&
                       filteredGuests.length > 0
                     }
                     onChange={handleSelectAllChange}
                     className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                   />
                   <span className="ml-2 text-xs font-medium text-gray-500">All</span>
                 </div>
                 <div 
                   className="text-sm font-medium cursor-pointer flex items-center"
                   onClick={() => handleSort("name")}
                 >
                   Name {renderSortIcon("name")}
                 </div>
                 <div 
                   className="text-sm font-medium cursor-pointer flex items-center"
                   onClick={() => handleSort("plus")}
                 >
                   Plus {renderSortIcon("plus")}
                 </div>
                 <div 
                   className="text-sm font-medium cursor-pointer flex items-center"
                   onClick={() => handleSort("via")}
                 >
                   Via {renderSortIcon("via")}
                 </div>
                 <div 
                   className="text-sm font-medium cursor-pointer flex items-center"
                   onClick={() => handleSort("admission")}
                 >
                   Admission {renderSortIcon("admission")}
                 </div>
                 <div 
                   className="text-sm font-medium cursor-pointer flex items-center"
                   onClick={() => handleSort("category")}
                 >
                   Category {renderSortIcon("category")}
                 </div>
                 <div className="text-sm font-medium text-center">Actions</div>
               </div>

               {/* Guest list items */}
               <div>
                {filteredGuests.map((guest) => {
                  const hasDiscountAdmission = isPercentageDiscountAdmission(guest.admission);

                  return (
                    <div
                      key={guest.id}
                      className={`
                        sm:grid sm:grid-cols-[40px_2fr_1fr_1.5fr_1fr_1fr_80px]
                        flex flex-col
                        border-b border-gray-200 hover:bg-gray-50 transition duration-200
                        ${guest.checked_in ? "bg-green-50" : ""}
                      `}
                    >
                     {/* Mobile layout */}
                     <div className="sm:hidden w-full py-1 px-3 flex justify-between items-start">
                       <div className="flex-1">
                         <div className={`font-medium leading-tight flex items-center ${guest.checked_in ? "line-through text-green-700" : "text-gray-900"}`}>
                           {renderCategoryIcon(guest.category)}
                           <span className="ml-1">{guest.name}</span>
                           {guest.checked_in && (
                             <div className="ml-1 text-xs text-green-600 flex items-center leading-none">
                               <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                               </svg>
                             </div>
                           )}
                         </div>
                         <div className="text-xs text-gray-500 leading-tight">
                           Plus: {guest.plus} | Via: {guest.type || "N/A"} |{" "}
                           <span className={hasDiscountAdmission ? "text-red-600 font-semibold" : ""}>
                             {guest.admission}
                           </span>{" "}
                           | {guest.category || "REGULAR"}
                         </div>
                       </div>
                       <div className="flex space-x-2 items-center">
                         <input
                           type="checkbox"
                           className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                           checked={selectedGuestIds.includes(guest.id)}
                           onChange={() => handleCheckboxChange(guest.id)}
                           onClick={(e) => e.stopPropagation()}
                         />
                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             handleEditGuest(guest);
                           }}
                           className="text-gray-500 hover:text-indigo-600"
                         >
                           <PencilSquareIcon className="h-4 w-4" />
                         </button>
                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             handleDeleteClick(guest.id);
                           }}
                           className="text-gray-500 hover:text-red-600"
                         >
                           <TrashIcon className="h-4 w-4" />
                         </button>
                       </div>
                     </div>

                     {/* Desktop layout */}
                     <div className="hidden sm:flex justify-center items-center py-1 px-2">
                       <input
                         type="checkbox"
                         className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                         checked={selectedGuestIds.includes(guest.id)}
                         onChange={() => handleCheckboxChange(guest.id)}
                         onClick={(e) => e.stopPropagation()}
                       />
                     </div>
                     <div className="hidden sm:flex items-center py-1 px-2">
                       <div className="leading-tight flex items-center">
                         {renderCategoryIcon(guest.category)}
                         <span className={`ml-1 ${guest.checked_in ? "line-through text-green-700" : "text-gray-900"}`}>
                           {guest.name}
                         </span>
                         {guest.checked_in && (
                           <span className="ml-2 inline-flex items-center text-xs text-green-600">
                             <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                             </svg>
                           </span>
                         )}
                       </div>
                     </div>
                     <div className={`hidden sm:flex items-center py-1 px-2 ${guest.checked_in ? "text-green-700" : "text-gray-700"}`}>
                       {guest.plus}
                     </div>
                     <div className={`hidden sm:flex items-center py-1 px-2 ${guest.checked_in ? "text-green-700" : "text-gray-700"}`}>
                       {guest.type || "N/A"}
                     </div>
                     <div
                       className={`hidden sm:flex items-center py-1 px-2 ${
                         hasDiscountAdmission
                           ? "text-red-600 font-semibold"
                           : guest.checked_in
                             ? "text-green-700"
                             : "text-gray-700"
                       }`}
                     >
                       {guest.admission}
                     </div>
                     <div className={`hidden sm:flex items-center py-1 px-2 ${guest.checked_in ? "text-green-700" : "text-gray-700"}`}>
                       {guest.category || "REGULAR"}
                     </div>
                     <div className="hidden sm:flex items-center justify-center space-x-1 py-1 px-2">
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           handleEditGuest(guest);
                         }}
                         className="text-gray-500 hover:text-indigo-600"
                       >
                         <PencilSquareIcon className="h-4 w-4" />
                       </button>
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           handleDeleteClick(guest.id);
                         }}
                         className="text-gray-500 hover:text-red-600"
                       >
                         <TrashIcon className="h-4 w-4" />
                       </button>
                     </div>
                    </div>
                  );
                })}
              </div>
            </>
           )}
         </div>

         {/* Modals */}
         {showConfirmModal && (
           <ConfirmModal
             isOpen={showConfirmModal}
             onClose={() => setShowConfirmModal(false)}
             onConfirm={handleDeleteGuest}
             message="Are you sure you want to delete this guest?"
           />
         )}

         {showConfirmModalMultiple && (
           <ConfirmModal
             isOpen={showConfirmModalMultiple}
             onClose={() => setShowConfirmModalMultiple(false)}
             onConfirm={handleDeleteSelected}
             message={`Are you sure you want to delete ${selectedGuestIds.length} selected guests?`}
           />
         )}

         {showModal && (
           <AddGuestModal
             onClose={handleCloseModal}
             onSave={handleSaveGuestList}
             currentUserName={user?.name || "N/A"}
           />
         )}

         {showEditModal && guestToEdit && (
           <EditGuestModal
             guestData={guestToEdit}
             onClose={() => setShowEditModal(false)}
             onUpdate={handleUpdateGuest}
           />
         )}

         <AddFromPermanentListModal
           isOpen={showAddFromListModal}
           onClose={handleCloseAddFromListModal}
           permanentGuestLists={permanentGuestListsOptions}
           onAddGuestsFromList={handleAddGuestsFromList}
         />
       </div>
     </EventLayout>
   </>
 );
}

export default GuestListPage;
